"use client";;
/* eslint-disable */

import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { isCategorySlug } from '../../i18n/categorySlugTranslations';
import { SearchBar } from '../SearchBar/SearchBar';
import type { NavTranslations, CountrySelectorTranslations } from '../../i18n/translationProps';
import { CurrencySelector } from '../CurrencySelector';
import { useCart } from '../../context/CartContext';
import { useSelectedCurrency } from '../../hooks';
import { SITE_CONFIG } from '@/constants';
import fullLogoBlack from '../../assets/images/full-logo-black.svg';
import fullLogoWhite from '../../assets/images/full-logo-white.svg';
import { useTheme } from '../../theme/ThemeProvider';
import { CountrySelector } from '../CountrySelector/CountrySelector';
import { LanguagePicker } from '../LanguagePicker/LanguagePicker';
import { ShoppingCartIcon, HomeOutlinedIcon, InfoOutlinedIcon, ContactPageOutlinedIcon, HelpOutlineIcon, StorefrontOutlinedIcon, PersonOutlinedIcon, FavoriteBorderIcon } from '../icons';
import { useAuth } from '../../context/AuthContext';
import { ThemeToggle } from '../../theme/ThemeToggle';
import styles from './Navigation.module.css';

const MobileCategoriesSubmenu = dynamic(() => import('./MobileCategoriesSubmenu').then(m => ({ default: m.MobileCategoriesSubmenu })));
const DesktopCategoriesDropdown = dynamic(() => import('./DesktopCategoriesDropdown').then(m => ({ default: m.DesktopCategoriesDropdown })));

interface NavigationProps {
  translations: NavTranslations;
  countrySelectorTranslations: CountrySelectorTranslations;
}

export const Navigation: React.FC<NavigationProps> = ({ translations, countrySelectorTranslations }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shouldRenderMobileMenu, setShouldRenderMobileMenu] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [giftcardsHovered, setGiftcardsHovered] = useState(false);
  const pathname = usePathname();
  const { getTotalItems, cart } = useCart();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const { selectedCurrency, updateCurrency } = useSelectedCurrency();
  const { locale, localizeFullPath, getCanonical } = useLocalizedPath();
  const t = translations;

  const canonicalPath = useMemo(() => {
    if (!pathname) return '/';
    const segments = pathname.split('/').filter(Boolean);
    const pathWithoutLocale = segments[0] === locale ? '/' + segments.slice(1).join('/') || '/' : pathname;
    return getCanonical(pathWithoutLocale);
  }, [pathname, locale, getCanonical]);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setShouldRenderMobileMenu(mobileMenuOpen);
  }, [mobileMenuOpen]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const handleCurrencyChange = useCallback((currency: string) => {
    updateCurrency(currency);
  }, [updateCurrency]);

  const isGiftcardsListingPage = useMemo(() => {
    if (canonicalPath === '/giftcards') return true;
    if (canonicalPath.startsWith('/giftcards/')) {
      const slug = canonicalPath.split('/')[2];
      return slug ? isCategorySlug(slug, locale) : false;
    }
    return false;
  }, [canonicalPath]);

  const isRegionSelectorsDisabled = useMemo(() => {
    return canonicalPath !== '/' && !isGiftcardsListingPage;
  }, [canonicalPath, isGiftcardsListingPage]);

  const isCurrencySelectorsDisabled = useMemo(() => {
    if (canonicalPath === '/' || canonicalPath.startsWith('/cart') || canonicalPath.startsWith('/giftcards')) return false;
    return true;
  }, [canonicalPath]);

  const cartBadge = useMemo(() => {
    const count = getTotalItems();
    return count > 0 ? <span className={styles.cartCount}>{count}</span> : null;
  }, [cart, getTotalItems]);

  const selectorSlot = useMemo(() => (
    <div className={styles.selectorSlot}>
      <Suspense fallback={null}>
        <CountrySelector disabled={isRegionSelectorsDisabled} translations={countrySelectorTranslations} />
      </Suspense>
      <CurrencySelector onCurrencyChange={handleCurrencyChange} disabled={isCurrencySelectorsDisabled} />
      <Suspense fallback={null}>
        <LanguagePicker />
      </Suspense>
    </div>
  ), [isRegionSelectorsDisabled, isCurrencySelectorsDisabled, handleCurrencyChange]);

  const topNavigation = useMemo(() => {
    return (
      <div className={styles.searchAndControls}>
        <Link href={localizeFullPath('/')} className={styles.logoLink} style={{ textDecoration: 'none' }} onClick={() => setMobileMenuOpen(false)}>
          <div className={styles.logo}>
            {/* height:auto matches the Footer logo. The global
                `img { height: auto }` in globals.css overrides the height prop
                but not the width, which is exactly the mismatch next/image
                warns about on every render. The logo's intrinsic ratio is
                270.2:54.91, so 150w resolves to ~30.5h - the same size it
                already rendered at. */}
            <Image
              src={resolvedTheme === 'dark' ? fullLogoWhite : fullLogoBlack}
              alt="ACEB Logo"
              width={150}
            height={30}
            style={{ height: 'auto' }}
            priority
            loading='eager'
            unoptimized
          />
          </div>
        </Link>

        {/* One field for all widths: in the row on desktop, a second row on
            phones (Navigation.module.css). */}
        <div className={styles.searchContainer}>
          <SearchBar placeholder={t.searchPlaceholder} onNavigate={closeMobileMenu} />
        </div>

        <div className={styles.rightControls}>
          {selectorSlot}

          <ThemeToggle />

          <Link href={localizeFullPath('/account/wishlist')} className={styles.cartWrapper} onClick={closeMobileMenu} aria-label={t.wishlist}>
            <FavoriteBorderIcon />
          </Link>

          <Link href={localizeFullPath(user ? '/account' : '/login')} className={styles.cartWrapper} onClick={closeMobileMenu} aria-label={user ? (user.username || user.email || t.account) : t.account}>
            {user ? (
              <div className={styles.avatarInitial} aria-hidden="true">
                {(user.username || user.email || '?').charAt(0).toUpperCase()}
              </div>
            ) : (
              <PersonOutlinedIcon />
            )}
          </Link>

          <Link href={localizeFullPath('/cart')} className={styles.cartWrapper} onClick={closeMobileMenu} aria-label={t.cart}>
            <ShoppingCartIcon />
            {cartBadge}
          </Link>

          <button
            className={`${styles.mobileMenuButton}${mobileMenuOpen ? ` ${styles.open}` : ''}`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? t.closeMenu : t.openMenu}
            aria-expanded={mobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
    );
  }, [mobileMenuOpen, cartBadge, closeMobileMenu, selectorSlot, toggleMobileMenu, localizeFullPath, t, user]);

  return (
    <>
      <header className={`${styles.navigationWrapper}${scrolled ? ` ${styles.scrolled}` : ''}`}>
        <div className={styles.scrolledNav}>
          {topNavigation}
          
          <div className={styles.scrolledLinks}>
            <div
              className={styles.navItemWithDropdown}
              onMouseEnter={() => setGiftcardsHovered(true)}
              onMouseLeave={() => setGiftcardsHovered(false)}
            >
              <Link href={localizeFullPath('/giftcards')} className={`${styles.navItem}${(pathname?.includes('/giftcards') || pathname?.includes('/carduri-cadou') || pathname?.includes('/tarjetas-regalo') || pathname?.includes('/cartes-cadeaux') || pathname?.includes('/geschenkkarten')) ? ` ${styles.active}` : ''}`}>{t.giftCards}</Link>
              <DesktopCategoriesDropdown isOpen={giftcardsHovered} categoryNames={t.categoryNames} />
            </div>
            <Link href={localizeFullPath('/brands')} className={`${styles.navItem}${(pathname?.includes('/brands') || pathname?.includes('/marcas') || pathname?.includes('/marques') || pathname?.includes('/marken') || pathname?.includes('/branduri')) ? ` ${styles.active}` : ''}`}>{t.brands}</Link>
            <Link href={localizeFullPath('/about')} className={`${styles.navItem}${(pathname?.includes('/about') || pathname?.includes('/despre') || pathname?.includes('/sobre') || pathname?.includes('/a-propos') || pathname?.includes('/ueber-uns')) ? ` ${styles.active}` : ''}`}>{t.aboutUs}</Link>
            <Link href={localizeFullPath('/blog')} className={`${styles.navItem}${pathname?.includes('/blog') ? ` ${styles.active}` : ''}`}>{t.blog}</Link>
            <Link href={localizeFullPath('/contact')} className={`${styles.navItem}${(pathname?.includes('/contact') || pathname?.includes('/kontakt') || pathname?.includes('/contacto') || pathname?.includes('/contato')) ? ` ${styles.active}` : ''}`}>{t.contact}</Link>
            <Link href={localizeFullPath('/faq')} className={`${styles.navItem}${(pathname?.includes('/faq') || pathname?.includes('/intrebari-frecvente') || pathname?.includes('/preguntas-frecuentes') || pathname?.includes('/perguntas-frequentes')) ? ` ${styles.active}` : ''}`}>{t.faq}</Link>
          </div>
        </div>

        {shouldRenderMobileMenu && (
          <div className={`${styles.mobileMenuOverlay}${mobileMenuOpen ? ` ${styles.open}` : ''}`}>
            <div className={styles.mobileSelectorsColumn}>
              <CountrySelector variant="full" disabled={isRegionSelectorsDisabled} translations={countrySelectorTranslations} />
              <CurrencySelector variant="full" onCurrencyChange={handleCurrencyChange} disabled={isCurrencySelectorsDisabled} />
              <LanguagePicker variant="full" />
              <ThemeToggle variant="full" />
            </div>

            <Link href={localizeFullPath('/')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <HomeOutlinedIcon fontSize="small" /> {t.home}
            </Link>
            
            <MobileCategoriesSubmenu
              categoriesOpen={categoriesOpen}
              setCategoriesOpen={setCategoriesOpen}
              onCategoryClick={() => {
                setMobileMenuOpen(false);
                setCategoriesOpen(false);
              }}
              giftCardsLabel={t.giftCards}
              categoryNames={t.categoryNames}
            />
            <Link href={localizeFullPath('/brands')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <StorefrontOutlinedIcon fontSize="small" /> {t.brands}
            </Link>
            <Link href={localizeFullPath('/about')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <InfoOutlinedIcon fontSize="small" /> {t.aboutUs}
            </Link>
            <Link href={localizeFullPath('/blog')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <InfoOutlinedIcon fontSize="small" /> {t.blog}
            </Link>
            <Link href={localizeFullPath('/faq')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <HelpOutlineIcon fontSize="small" /> {t.faq}
            </Link>
            <Link href={localizeFullPath('/contact')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <ContactPageOutlinedIcon fontSize="small" /> {t.contact}
            </Link>
            <Link href={localizeFullPath(user ? '/account' : '/login')} className={styles.mobileMenuItem} onClick={closeMobileMenu}>
              <PersonOutlinedIcon fontSize="small" /> {user ? user.username : t.account}
            </Link>
          </div>
        )}
      </header>
    </>
  );
};